import React, { useContext, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { message, Modal } from "antd";
import { Input } from "../../src/ui/Input";
import { Card } from "../../src/ui/Card";
import TextField from "../ui/TextField";
import { EyeIcon, EyeOff } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import axios from "../axios/AxiosInstance";
import { restoreAgency } from "../services/agencyPreference";
import { getPostLoginPath } from "../services/authSession";

export default function LoginPage() {
  const { token } = useParams();
  const { setIsLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const postLoginPath = getPostLoginPath(location.search);
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    if (token) {
      axios
        .post("auth/check/freelance/login", { token })
        .then(async (res) => {
          localStorage.setItem("isLogged", "true");
          localStorage.setItem("accessToken", res.data.access_token);
          localStorage.setItem("agencyName", res.data.agencyName);
          localStorage.setItem("agencyCity", res.data.agencyCity || "");
          localStorage.setItem("refreshToken", res.data.refresh_token);
          localStorage.setItem("userEmail", res.data.email);
          localStorage.setItem("userRole", res.data.role);
          localStorage.setItem("userId", res.data._id);
          localStorage.setItem("firstname", res.data.firstname);
          localStorage.setItem("lastname", res.data.lastname);
          if (!(await restoreAgency())) message.warning("Votre dernière agence n’a pas pu être restaurée. Vous pouvez la sélectionner dans le menu.");
          setIsLoggedIn(true);
          navigate(postLoginPath, { replace: true });
        })
        .catch((e) => {
          console.log("Token invalide");
          message.error("Token invalide");
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (event) => {
    event?.preventDefault();
    if (busy) return;
    setBusy(true); setAuthError("");
    try {
      const {data} = await axios.post("/auth/freelance/login", {username:email,password});
      if (!data?.requiresTwoFactor || !data.challengeId) throw new Error("La double authentification n’a pas pu démarrer.");
      setChallenge(data); setCode(""); setOpenModal(true);
    } catch (error) {
      const detail = error.response?.status === 401 ? "Identifiant ou mot de passe incorrect." : error.response?.data?.message || error.message || "Connexion impossible. Réessayez.";
      setAuthError(Array.isArray(detail) ? detail.join(" ") : detail);
    } finally { setBusy(false); }
  };
  const validateCode = async (event) => {
    event.preventDefault();
    if (busy || !challenge || !/^\d{6}$/.test(code)) return;
    setBusy(true); setAuthError("");
    try {
      const {data} = await axios.post("auth/freelance/validate-code",{challengeId:challenge.challengeId,code},{skipAuthFailureHandling:true});
      if (!data?.access_token || !data.refresh_token) throw new Error("Connexion non confirmée.");
      const values = {isLogged:"true",accessToken:data.access_token,refreshToken:data.refresh_token,agencyName:data.agencyName,agencyCity:data.agencyCity||"",userEmail:data.username,userRole:data.role,userId:data._id,firstname:data.firstname,lastname:data.lastname,phone:data.phone||""};
      Object.entries(values).forEach(([key,value])=>localStorage.setItem(key,value ?? ""));
      setPassword(""); setCode(""); setOpenModal(false);
      if (!(await restoreAgency())) message.warning("Votre dernière agence n’a pas pu être restaurée. Vous pouvez la sélectionner dans le menu.");
      setIsLoggedIn(true); navigate(postLoginPath,{replace:true});
    } catch (error) {setAuthError(error.response?.data?.message || "Code invalide ou expiré. Réessayez ou demandez un nouveau code.");}
    finally {setBusy(false);}
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-tr from-gray-50 to-gray-100 p-4">
      {openModal && <Modal centered title="Vérification de connexion" open footer={null} onCancel={()=>{if(!busy){setOpenModal(false);setCode("");setAuthError("");}}} maskClosable={!busy} closable={!busy}>
        <form onSubmit={validateCode} className="space-y-4 pt-4">
          <p>Entrez le code à 6 chiffres envoyé à <strong>{challenge?.emailHint}</strong>. Il est valable 10 minutes.</p>
          <Input autoFocus aria-label="Code de connexion" autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={event=>setCode(event.target.value.replace(/\D/g,""))} className="h-12 text-center text-xl tracking-widest" />
          {authError && <p role="alert" className="text-sm text-red-700">{authError}</p>}
          <button type="submit" disabled={busy || code.length !== 6} className="w-full rounded-lg bg-slate-800 p-3 font-semibold text-white disabled:opacity-50">{busy ? "Vérification…" : "Valider le code"}</button>
          <button type="button" disabled={busy} onClick={login} className="w-full p-2 text-sm text-slate-600 underline disabled:opacity-50">Renvoyer un code</button>
        </form>
      </Modal>}
      <div className="w-full max-w-5xl flex rounded-3xl overflow-hidden shadow-xl">
        {/* Partie gauche - Formulaire de connexion */}
        <div className="w-full lg:w-2/5 bg-gray-50 p-10 flex flex-col justify-center">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-semibold text-gray-700 mb-4">
              J'M <span className="text-gray-600">INTERIM</span>
            </h1>
          </div>

          <Card className="border-none shadow-none bg-transparent">
            <form onSubmit={login} className="space-y-6">
              {!openModal && authError && <p role="alert" className="text-sm text-red-700">{authError}</p>}
              <div className="space-y-2">
                <div className="relative">
                  <TextField
                    name="email"
                    className="text-blue-500"
                    onChange={(e) => setEmail(e.target.value)}
                    aria-label="Identifiant"
                    type="text"
                    placeholder="Identifiant"
                    icon="bi:envelope-open"
                    width={14}
                  />
                  <div className="absolute left-3 top-3.5 text-gray-400">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="relative">
                  <TextField
                    name="password"
                    className="text-blue-500"
                    onChange={(e) => setPassword(e.target.value)}
                    aria-label="Mot de passe"
                    type={isVisible ? "text" : "password"}
                    onClick={() => setIsVisible(!isVisible)}
                    icon_psw={
                      isVisible
                        ? "mdi:EyeIcon-outline"
                        : "mdi:EyeIcon-off-outline"
                    }
                    placeholder="Mot de passe"
                    icon={"ri:lock-2-line"}
                    // width={17}
                  />
                  <div className="absolute left-3 top-3.5 text-gray-400">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    {isVisible ? (
                      <EyeIcon
                        className="h-5 w-5"
                        onClick={() => setIsVisible(false)}
                      />
                    ) : (
                      <EyeOff
                        className="h-5 w-5"
                        onClick={() => setIsVisible(true)}
                      />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="w-full h-12 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white rounded-xl"
              >
                Connexion
              </button>

              <div className="text-center">
                <a
                  href="/forgot-password"
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Mot de passe oublié ?
                </a>
              </div>
            </form>
          </Card>
        </div>

        {/* Partie droite - Slogan */}
        <div className="hidden lg:block lg:w-3/5 bg-gradient-to-br from-gray-800 to-gray-900 relative overflow-hidden">
          <div className="h-full flex flex-col items-center justify-center p-10 text-center">
            <div className="relative max-w-xl">
              <div className="text-white text-3xl font-light leading-tight tracking-wide mb-6">
                <span className=" mb-4">Derrière chaque réussite,</span>
                <p className="flex space-x-1 justify-center">
                  <span>une équipe</span>
                  <span className=" text-white font-semibold mr-1">
                    J'm intérim
                  </span>
                </p>
                <span className=" text-gray-300 mt-4">engagée !</span>
              </div>

              <div className="w-32 h-1 mx-auto bg-gradient-to-r from-gray-300 to-gray-400 opacity-60 mb-10"></div>

              {/*    <div className="text-gray-400 italic text-lg md:text-xl">
                  Votre partenaire de confiance pour l'emploi temporaire
                </div> */}
            </div>

            {/* Décoration géométrique */}
            <div className="absolute -bottom-10 -right-10 w-40 h-40 border-[16px] border-gray-700 rounded-full opacity-30"></div>
            <div className="absolute -top-10 -left-10 w-40 h-40 border-[16px] border-gray-700 rounded-full opacity-30"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
